import {
    BrandBalance,
    DomainBalance,
    MultiBrandWalletInfo,
    RawWalletTransactionsResponse,
    WalletBrandSource,
    WalletInfoResponse,
    WalletInfoResponseWrapped,
    WalletTransaction,
    WalletTransactionsResponse,
    WalletTransactionsResponseWrapped
} from '@/src/types/wallet'
import { mapCleartripRawTransaction, mapRawTransaction } from '@/src/utils/wallet/transactionMapper'

export const isWrappedWalletInfoResponse = (r: unknown): r is WalletInfoResponseWrapped =>
    typeof r === 'object' && r !== null && ('MAIN' in r || 'LINKED' in r)

export const isWrappedWalletTransactionsResponse = (r: unknown): r is WalletTransactionsResponseWrapped =>
    typeof r === 'object' && r !== null && !Array.isArray(r) && ('MAIN' in r || 'LINKED' in r)

const resolveWalletInfoResponse = (value: WalletInfoResponse | string | undefined): WalletInfoResponse | undefined => {
    if (typeof value === 'string' || value === null) return undefined
    return value
}

/**
 * Extracts the balance for a given currency from a WalletInfoResponse.
 * Handles the case where status is null (linked wallet with balance but no explicit status).
 * Priority: domainBalances match > root balance.
 */
const resolveBrandBalanceForCurrency = (
    response: WalletInfoResponse | undefined,
    fallbackCurrency?: string
): { balance: number; currency: string } => {
    if (!response) return { balance: 0, currency: fallbackCurrency ?? '' }

    // Only treat as empty when explicitly FAILURE
    if (response.status === 'FAILURE') {
        return { balance: 0, currency: response.currency ?? fallbackCurrency ?? '' }
    }

    const resolvedCurrency = response.currency ?? fallbackCurrency ?? ''
    const domainMatch = response.domainBalances?.find(db => db.currency === resolvedCurrency)
    if (domainMatch) {
        return { balance: domainMatch.balance ?? 0, currency: domainMatch.currency ?? resolvedCurrency }
    }

    return { balance: response.balance ?? 0, currency: resolvedCurrency }
}

/**
 * Returns domain balances for a WalletInfoResponse.
 * If domainBalances is null/empty but a root balance exists, synthesizes a single entry
 * so that the carousel shows the Cleartrip tile even when the API omits domainBalances.
 */
const buildDomainBalancesFromResponse = (info: WalletInfoResponse | undefined): DomainBalance[] => {
    if (!info || info.status === 'FAILURE') return []

    if (info.domainBalances?.length) return info.domainBalances

    // Synthesize from root balance + currency when domainBalances is absent
    if (info.balance != null && info.balance > 0 && info.currency) {
        return [{ balance: info.balance, currency: info.currency }]
    }

    return []
}

export const buildBrandBalances = (
    main?: WalletInfoResponse | string,
    linked?: WalletInfoResponse | string,
    currency?: string
): BrandBalance[] => {
    const mainInfo = resolveWalletInfoResponse(main)
    const linkedInfo = resolveWalletInfoResponse(linked)
    const mainResolved = resolveBrandBalanceForCurrency(mainInfo, currency)
    const linkedResolved = resolveBrandBalanceForCurrency(linkedInfo, currency)

    return [
        {
            brandSource: 'MAIN',
            brandLabel: 'Flyin Wallet',
            balance: mainResolved.balance,
            currency: mainResolved.currency
        },
        {
            brandSource: 'LINKED',
            brandLabel: 'Cleartrip Wallet',
            balance: linkedResolved.balance,
            currency: linkedResolved.currency
        }
    ]
}

export const mergeWalletInfoResponses = (
    main?: WalletInfoResponse | string,
    linked?: WalletInfoResponse | string,
    fallbackCurrency?: string
): MultiBrandWalletInfo => {
    const mainInfo = resolveWalletInfoResponse(main)
    const linkedInfo = resolveWalletInfoResponse(linked)

    const mainResolved = resolveBrandBalanceForCurrency(mainInfo, fallbackCurrency)
    const linkedResolved = resolveBrandBalanceForCurrency(linkedInfo, fallbackCurrency)
    const mainBalance = mainResolved.balance
    const linkedBalance = linkedResolved.balance
    const totalBalance = mainBalance + linkedBalance

    const currency = mainResolved.currency || linkedResolved.currency || fallbackCurrency || ''

    const mainDomainBalances = buildDomainBalancesFromResponse(mainInfo)

    const brandBalances = buildBrandBalances(mainInfo, linkedInfo, currency)

    return {
        status: (mainInfo?.status === 'SUCCESS' || mainInfo?.balance != null && mainInfo.balance > 0)
            || (linkedInfo?.status === 'SUCCESS' || linkedInfo?.balance != null && linkedInfo.balance > 0)
            ? 'SUCCESS'
            : 'FAILURE',
        balance: totalBalance,
        currency,
        // Carousel should use Flyin (MAIN) domain balances only.
        domainBalances: mainDomainBalances,
        brandBalances,
        mainBalance,
        linkedBalance,
        message: mainInfo?.message ?? linkedInfo?.message
    }
}

const tagTransactions = (transactions: WalletTransaction[], brandSource: WalletBrandSource): WalletTransaction[] =>
    transactions.map(txn => ({ ...txn, brandSource }))

const mapAndTagRawTransactions = (
    response: RawWalletTransactionsResponse | any[] | string | undefined,
    brandSource: WalletBrandSource
): { transactions: WalletTransaction[]; total: number } => {
    // New BFF format: MAIN / LINKED are direct arrays of Cleartrip-format items
    if (Array.isArray(response)) {
        const mapped = response.map(t => ({ ...mapCleartripRawTransaction(t), brandSource }))
        return { transactions: mapped, total: response.length }
    }

    // Empty string means no wallet of this brand
    if (typeof response === 'string' || !response) {
        return { transactions: [], total: 0 }
    }

    // Legacy format: { totalRecords, transactions: [] }
    if (!response.transactions?.length) {
        return { transactions: [], total: 0 }
    }

    const mapped = response.transactions.map(mapRawTransaction)
    const tagged = tagTransactions(mapped, brandSource)
    return { transactions: tagged, total: response.totalRecords ?? 0 }
}

export const mergeWalletTransactions = (
    mainResponse?: RawWalletTransactionsResponse | any[] | string,
    linkedResponse?: RawWalletTransactionsResponse | any[] | string,
    remainingCount: number = 0
): WalletTransactionsResponse => {
    const main = mapAndTagRawTransactions(mainResponse, 'MAIN')
    const linked = mapAndTagRawTransactions(linkedResponse, 'LINKED')

    const allTransactions = [...main.transactions, ...linked.transactions]

    allTransactions.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
        return dateB - dateA
    })

    return {
        status: 'SUCCESS',
        transactions: allTransactions,
        totalRecords: main.total + linked.total + remainingCount
    }
}
